/**
 * Validate and normalize shape returned from LLM.
 * Ensures required keys exist, applies defaults where needed,
 * and logs differences for debugging.
 */
export function validateShape(shape, { debug = true } = {}) {
  if (debug) {
    console.groupCollapsed("🔍 validateShape: raw LLM response");
    console.log(JSON.stringify(shape, null, 2));
    console.groupEnd();
  }

  if (!shape || typeof shape !== "object") {
    return { valid: false, error: "No intent JSON received." };
  }

  const normalized = { ...shape }; // shallow copy

  // --- Intent ---
  if (!["summary", "compare", "machine_state"].includes(normalized.intent)) {
    if (debug) console.warn(`⚠️ Invalid intent "${normalized.intent}", defaulting to "summary"`);
    normalized.intent = "summary";
  }

  // --- useCurrentShift ---
  if (typeof normalized.useCurrentShift !== "boolean") {
    if (debug) console.warn("⚠️ useCurrentShift not boolean, defaulting to false");
    normalized.useCurrentShift = false;
  }

  // --- Tenant/Site ---
  if (!normalized.tenantId || typeof normalized.tenantId !== "string") {
    if (debug) console.warn("⚠️ tenantId missing/invalid, defaulting to machinetracking:default");
    normalized.tenantId = "machinetracking:default";
  }
  if (!normalized.siteId || typeof normalized.siteId !== "string") {
    if (debug) console.warn("⚠️ siteId missing/invalid, defaulting to default-site");
    normalized.siteId = "default-site";
  }

  // --- ShiftName ---
  if (!normalized.shiftName) {
    if (debug) console.warn("⚠️ shiftName missing, defaulting to Shift 1");
    normalized.shiftName = "Shift 1";
  }

  // --- Ranges ---
  if (!Array.isArray(normalized.ranges)) {
    if (debug) console.warn("⚠️ ranges missing/invalid, defaulting to empty array");
    normalized.ranges = [];
  }
  normalized.ranges = normalized.ranges.filter(
    (r) => r && r.dateStart && r.dateEnd
  );

  // --- Metric ---
  if (
    !["part_count_total", "utilization_pct", "downtime_minutes", "unlabeled_minutes", null].includes(
      normalized.metric
    )
  ) {
    if (debug) console.warn(`⚠️ Invalid metric "${normalized.metric}", defaulting to part_count_total`);
    normalized.metric = "part_count_total";
  }

  // --- group_by ---
  if (!Array.isArray(normalized.group_by)) {
    if (debug) console.warn("⚠️ group_by invalid, defaulting to []");
    normalized.group_by = [];
  }

  // --- top_n ---
  if (typeof normalized.top_n !== "number" || normalized.top_n <= 0) {
    if (debug) console.warn(`⚠️ top_n invalid "${normalized.top_n}", defaulting to 3`);
    normalized.top_n = 3;
  }

  // --- intent_detail ---
  if (!normalized.intent_detail || typeof normalized.intent_detail !== "object") {
    if (debug) console.warn("⚠️ intent_detail missing/invalid, defaulting");
    normalized.intent_detail = { machineId: "", statusFilter: "any" };
  }
  if (typeof normalized.intent_detail.machineId !== "string") {
    if (debug) console.warn("⚠️ intent_detail.machineId invalid, defaulting to empty string");
    normalized.intent_detail.machineId = "";
  }
  if (!["on", "off", "all", "any"].includes(normalized.intent_detail.statusFilter)) {
    if (debug) console.warn(`⚠️ Invalid statusFilter "${normalized.intent_detail.statusFilter}", defaulting to "any"`);
    normalized.intent_detail.statusFilter = "any";
  }

  if (debug) {
    console.groupCollapsed("✅ validateShape: normalized output");
    console.log(JSON.stringify(normalized, null, 2));
    console.groupEnd();
  }

  return { valid: true, shape: normalized };
}
